import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FaCopy, FaUserPlus, FaRupeeSign, FaUsers } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import { API_URL } from '../../utils/api';

const MyTeam = () => {
  const [teamData, setTeamData] = useState({
    referralCode: '',
    totalReferrals: 0,
    team: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/referral/my-team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamData(response.data);
    } catch (error) {
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${teamData.referralCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  const handleTransferProfit = async () => {
    const available = (teamData.totalReferralEarnings || 0) - (teamData.withdrawnReferralEarnings || 0);
    if (available <= 0) {
      toast.error('No earnings available to transfer');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/referral/transfer-profit`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to transfer earnings');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>My Team - Assets Dairy Farm</title></Helmet>
      <div className="bg-gray-50 min-h-screen py-8">
        <div className="container mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800">My Team</h1>
            <p className="text-gray-600 mt-1">Track your referral network</p>
          </div>

          <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl shadow-lg p-6 mb-8 text-white">
            <div className="flex flex-wrap justify-between items-center">
              <div>
                <p className="text-sm opacity-90">Your Referral Code</p>
                <p className="text-3xl font-bold mt-1">{teamData.referralCode}</p>
                <p className="text-xs opacity-80 mt-2">Share this code with friends</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={copyReferralLink}
                  className="bg-white text-green-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition flex items-center gap-2"
                >
                  <FaCopy /> Copy Link
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <FaUsers className="text-blue-600 text-xl" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Total Referrals</p>
                  <p className="text-2xl font-bold">{teamData.totalReferrals}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <FaRupeeSign className="text-green-600 text-xl" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Team Investment</p>
                  <p className="text-2xl font-bold">
                    ₹{teamData.team.reduce((sum, t) => sum + (t.totalInvestment || 0), 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6 flex flex-col justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center shrink-0">
                  <FaRupeeSign className="text-purple-600 text-xl" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Available Earnings</p>
                  <p className="text-2xl font-bold text-purple-600">
                    ₹{teamData.totalReferralEarnings ? ((teamData.totalReferralEarnings || 0) - (teamData.withdrawnReferralEarnings || 0)).toLocaleString(undefined, { maximumFractionDigits: 2 }) : '0'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Total Earned: ₹{(teamData.totalReferralEarnings || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                </div>
              </div>
              {((teamData.totalReferralEarnings || 0) - (teamData.withdrawnReferralEarnings || 0)) > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={handleTransferProfit}
                    className="w-full bg-purple-100 text-purple-700 py-2 rounded-lg font-semibold hover:bg-purple-200 transition text-sm"
                  >
                    Transfer to Wallet
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-800">My Referrals</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Investment</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {teamData.team.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                        No referrals yet. Share your referral code!
                      </td>
                    </tr>
                  ) : (
                    teamData.team.map((member, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4">
                          <div className="font-medium">{member.user?.name}</div>
                          <div className="text-xs text-gray-500">{member.user?.email}</div>
                        </td>
                        <td className="px-6 py-4 text-sm">{member.user?.userId}</td>
                        <td className="px-6 py-4 text-sm">{new Date(member.joinedAt).toLocaleDateString()}</td>
                        <td className="px-6 py-4 font-semibold text-green-600">
                          ₹{member.totalInvestment?.toLocaleString() || 0}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            member.user?.kycStatus === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {member.user?.kycStatus === 'approved' ? 'Active' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MyTeam;